import React, { ReactElement, ReactNode, useEffect } from "react";
import { RightPanel } from "components/rightPanel/RightPanel";
import {
  RightMenuItems,
  RightMenuItem,
  AlternateRightMenuItems,
} from "components/rightMenu/RightMenu";
import { RightMenu } from "components/rightMenu/RightMenu";
import { TableInfo } from "api";
import { DisableMenuItemData } from "pages/PlatformUsers/PlatformUsers";
import { ACTION_KEY } from "../../config";
import { useGetPermissions } from "../../helpers/useGetPermissions";
import { useHandleActionFromStorage } from "../../helpers/useHandleActionFromStorage";
import { TableOptionItem } from "components/RowOptions";

import { useDispatch, useSelector } from "react-redux";
import {
  resetNotificationItems,
  selectNotificationItems,
} from "reducers/workflowNotifications";
import StartNewWorkflow from "pages/homes/StartNewWorkflow";
import WorkflowNotification from "pages/workflowNotifications/WorkflowNotification";
import { homePermissionsRequested } from "App";
import { RolePermissions } from "./Category";

export const checkDisableMenuItemBasedOnSelectedDataValue = <T,>(
  permissionsKey: string | undefined,
  disableMenuItemData: DisableMenuItemData | undefined,
  selectedData: T[],
  rolePermissions?: RolePermissions<T> | undefined,
  oppositePermissions?: RolePermissions<T>
): boolean => {
  let disableMenuItemBasedOnSelectedDataValue = false;
  if (
    permissionsKey &&
    disableMenuItemData &&
    disableMenuItemData.hasOwnProperty(permissionsKey) &&
    disableMenuItemData[permissionsKey].fieldName !== undefined &&
    disableMenuItemData[permissionsKey].fieldValue !== undefined &&
    selectedData &&
    Array.isArray(selectedData) &&
    selectedData.length === 1 &&
    selectedData[0] &&
    typeof selectedData[0] === "object" &&
    selectedData[0].hasOwnProperty(
      disableMenuItemData[permissionsKey].fieldName
    ) &&
    selectedData[0][
      disableMenuItemData[permissionsKey].fieldName as keyof T
    ] === disableMenuItemData[permissionsKey].fieldValue
  ) {
    disableMenuItemBasedOnSelectedDataValue = true;
  }

  if (
    selectedData &&
    Array.isArray(selectedData) &&
    selectedData.length > 0 &&
    selectedData[0] &&
    typeof selectedData[0] === "object" &&
    rolePermissions &&
    permissionsKey
  ) {
    disableMenuItemBasedOnSelectedDataValue = selectedData.some(item => {
      return Object.entries(rolePermissions).some(([role, permissions]) => {
        return (
          item[role as keyof T] === false &&
          (permissions as string[]).includes(permissionsKey)
        );
      });
    });
    // if disblaed true then return else check any false flag
    if (!disableMenuItemBasedOnSelectedDataValue && oppositePermissions) {
      disableMenuItemBasedOnSelectedDataValue = selectedData.some(item => {
        return Object.entries(oppositePermissions).some(
          ([role, permissions]) => {
            return (
              item[role as keyof T] === true &&
              (permissions as string[]).includes(permissionsKey)
            );
          }
        );
      });
    }
  }

  return disableMenuItemBasedOnSelectedDataValue;
};

export type CRUDAction =
  | {
      type: "change_open_menu";
      payload: {
        crudOptions: CrudOptions;
        selectedId: React.ReactText | null;
        fromRowOption: boolean;
      };
    }
  | { type: "close_menus" };

type State = {
  currentMenu: CrudOptions;
  selectedId: React.ReactText | null;
  fromRowOption: boolean;
};

export const initialState: State = {
  currentMenu: null,
  selectedId: null,
  fromRowOption: false,
};

export type CallbackOrBoolean<Data> =
  | boolean
  | ((selectedItems: Data[]) => boolean);

export type CallbackOrString<Data> =
  | string
  | ((selectedItems: Data[]) => string);

export type CallbackOrPermissions<Data> =
  | string
  | ((selectedItems: Data[], permissions: Map<string, boolean>) => boolean);

export function reducer(extraActions?: () => void) {
  return (state: State, action: CRUDAction): State => {
    switch (action.type) {
      case "change_open_menu":
        return {
          ...state,
          currentMenu: action.payload.crudOptions,
          selectedId: action.payload.selectedId,
          fromRowOption: action.payload.fromRowOption,
        };
      case "close_menus": {
        extraActions && extraActions();
        return {
          ...state,
          currentMenu: null,
          selectedId: null,
          fromRowOption: false,
        };
      }
    }
  };
}

export type Menu = {
  currentMenu: CrudOptions;
  menuItems: RightMenuItems;
  selectedId: React.ReactText | null;
  selectedData: any;
  fromRowOption: boolean;
  //currentMenuRedirected?: boolean;
};

export type MenuState = [
  Menu,
  {
    openMenu(
      menu: CrudOptions,
      id: React.ReactText | null,
      fromRowOption?: boolean
    ): void;
    closeMenus(): void;
  }
];

export type MenuItem<Options = CrudOptions> = {
  icon: string;
  name: string;
  imgAlt: string;
  menuCode: Options;
  showOnMultiSelect: boolean;
  disabled?: boolean;
};

export type AlternateMenuItemOnClick = (
  dispatch: React.Dispatch<any>,
  selectedDataIDs: number[] | null,
  additionalDataIDs?: any
) => void;

export type CrudItems<Data, Options = CrudOptions> = {
  icon: CallbackOrString<Data>;
  imgAltText: CallbackOrString<Data>;
  menuCode: Options;
  showInMultiSelect: CallbackOrBoolean<Data>;
  permissionsKey?: string; //CallbackOrPermissions<Data>;

  title: CallbackOrString<Data>;
  content(
    onDone: () => void,
    selectedData: Data[],
    menu: Menu,
    additionalData?: any,
    clickedIdIfMultiIds?: string | number,
    menuActions?: MenuState[1]
  ): React.ReactNode;
  inRightPanel: CallbackOrBoolean<Data>;
  isDisabled: CallbackOrBoolean<Data>;
  notInMenus?: CallbackOrBoolean<Data>;
  doNotLoadContentInRightPanel?: boolean;
  alternateMenuItemOnClick?: AlternateMenuItemOnClick;

  customItem?: (
    props?: (Omit<RightMenuItem, "customItem"> | TableOptionItem) & {
      additionalDataIDs?: any;
    }
  ) => ReactElement;
}[];
/**
 * It might be a function, it might just be a boolean.
 */
export function applyMaybeBoolean<Data>(
  input: boolean | ((selectedItems: Data[]) => boolean),
  selectedData: Data[]
): boolean {
  if (typeof input === "boolean") return input;

  return input(selectedData);
}
/**
 * It might be a function, it might just be a string.
 */
export function applyMaybeString<Data>(
  input: string | ((selectedItems: Data[]) => string),
  selectedData: Data[]
): string {
  if (typeof input === "string") return input;

  return input(selectedData);
}

export function applyMaybePermission<Data>(
  input:
    | string
    | null
    | undefined
    | ((
        selectedItems: Data[],
        permissions: Map<string, boolean>
      ) => boolean) = "",
  selectedData: Data[],
  permissions: Map<string, boolean>
): boolean {
  if (!input) return true;
  if (typeof input === "string") return !!permissions.get(input);

  return input(selectedData, permissions);
}
export function applyMaybePermissionString<Data>(
  input: string | null | undefined | ((selectedItems: Data[]) => string) = "",
  selectedData: Data[]
): string {
  if (!input) return "";
  if (typeof input === "string") return input;

  return input(selectedData);
}

export type GenericFunction = (...props: any) => void;

interface UseMenuStateParams<T> {
  menuItems: CrudItems<T>;
  selectedData: T[];
  permissions: Map<string, boolean>;
  table?: TableInfo;
  idColumn?: string;
  disableMenuItemData?: DisableMenuItemData; // Field names and values that can also decides if a menu item is disabled or not
  rolePermissions?: RolePermissions<T> | undefined;
  oppositePermissions?: RolePermissions<T>;
}

export function useMenuState<Data>(props: UseMenuStateParams<Data>) {
  const {
    menuItems,
    selectedData: rawSelectedData,
    permissions,
    table,
    idColumn,
    disableMenuItemData,
    rolePermissions,
    oppositePermissions,
  } = props;
  const [store, dispatch] = React.useReducer(
    reducer(() => {}),
    initialState
  );

  const {
    currentMenu,
    selectedId,
    fromRowOption, //redirected
  } = store;

  const getSelectedData = (table: TableInfo): any => {
    if (selectedId && idColumn) {
      const found = table.data?.details.listData.find(
        (data: any) => data[idColumn] === selectedId
      );
      return found ? [found] : [];
    }

    return [];
  };

  const selectedData =
    fromRowOption && table ? getSelectedData(table) : rawSelectedData;

  const currentMenuRedirected = useHandleActionFromStorage(
    menuItems,
    selectedId,
    fromRowOption,
    dispatch
  );

  const openMenu = React.useCallback(
    (
      menu: CrudOptions,
      selectedId: React.ReactText | null,
      fromRowOption?: boolean
    ) => {
      dispatch({
        type: "change_open_menu",
        payload: {
          crudOptions: menu,
          selectedId: selectedId,
          fromRowOption: fromRowOption ?? false,
          //redirected: false,
        },
      });
    },
    [dispatch]
  );

  const closeMenus = React.useCallback(() => {
    dispatch({ type: "close_menus" });
  }, [dispatch]);

  const generateMenuItems = (menuItems: CrudItems<Data>): RightMenuItems => {
    const singleItems: RightMenuItem[] = menuItems.map(item => {
      const hasPermission =
        item.permissionsKey == null
          ? true
          : !!permissions.get(item.permissionsKey);

      const disableMenuItemBasedOnSelectedDataValue = checkDisableMenuItemBasedOnSelectedDataValue<
        Data
      >(
        item.permissionsKey as string | undefined,
        disableMenuItemData,
        selectedData,
        rolePermissions,
        oppositePermissions
      );

      const isDisabled =
        applyMaybeBoolean(item.isDisabled, selectedData) ||
        !hasPermission ||
        disableMenuItemBasedOnSelectedDataValue;

      const menuItemOnClick = isDisabled
        ? () => {} //or item.alternateMenuItemOnClick(dispatch, selectedData.filter(...).map(...))
        : () => openMenu(item.menuCode, null);

      return {
        icon: applyMaybeString(item.icon, selectedData),
        name: applyMaybeString(item.title, selectedData),
        imgAlt: applyMaybeString(item.imgAltText, selectedData),
        disabled: isDisabled,
        onClick: menuItemOnClick,
        notInMenus: item.notInMenus,
        alternateMenuItemOnClick: item.alternateMenuItemOnClick,
        customItem: item.customItem,
        permissionKey: item.permissionsKey,
      };
    });

    const multiItems: AlternateRightMenuItems = {
      menuItems: menuItems
        .filter(x => applyMaybeBoolean(x.showInMultiSelect, selectedData))
        .map(item => {
          const hasPermission =
            item.permissionsKey == null
              ? true
              : !!permissions.get(item.permissionsKey);

          const disableMenuItemBasedOnSelectedDataValue = checkDisableMenuItemBasedOnSelectedDataValue(
            item.permissionsKey as string | undefined,
            disableMenuItemData,
            selectedData,
            rolePermissions,
            oppositePermissions
          );
          const isDisabled =
            applyMaybeBoolean(item.isDisabled, selectedData) ||
            !hasPermission ||
            disableMenuItemBasedOnSelectedDataValue;
          return {
            icon: applyMaybeString(item.icon, selectedData),
            name: applyMaybeString(item.title, selectedData),
            imgAlt: applyMaybeString(item.imgAltText, selectedData),
            onClick: isDisabled
              ? () => {} //or item.alternateMenuItemOnClick(dispatch, selectedData.filter(...).map(...))
              : () => openMenu(item.menuCode, null),
            disabled: isDisabled,
            alternateMenuItemOnClick: item.alternateMenuItemOnClick,
            customItem: item.customItem,
            permissionKey: item.permissionsKey,
          };
        }),
    };

    return { menuItems: singleItems, alternateMenuItems: multiItems };
  };

  const finalMenuItems: RightMenuItems = generateMenuItems(menuItems);

  const output = React.useMemo(() => {
    const data = {
      currentMenu,
      menuItems: finalMenuItems,
      selectedId,
      selectedData,
      fromRowOption,
      //currentMenuRedirected,
    };

    const actions = { openMenu, closeMenus };
    return [data, actions] as MenuState;
  }, [
    openMenu,
    closeMenus,
    currentMenu,
    finalMenuItems,
    selectedId,
    fromRowOption,
    //currentMenuRedirected,
  ]);

  return output;
}

export type CrudOptions =
  | "CREATE"
  | "ADD"
  | "VIEW"
  | "UPDATE"
  | "DELETE"
  | "FILTERS"
  | "PORTAL_CONFIGURATION"
  | "DATE_SETTINGS"
  | "INTEGRATIONS"
  | "IMPORT_CLIENT_USERS"
  | "RESEND_INVITATION_EMAILS"
  | "RESEND_VERIFICATION_LINK"
  | "CREATE_BULK"
  | "VIEW_DRAWING"
  | "PROJECT_MANAGEMENT"
  | "DOWNLOAD"
  | "UPLOAD"
  | "MARK_AS_FAVOURITE"
  | "SNAGG"
  | "DATES"
  | "CLONE_GATEWAY"
  | "CLONE_WORKFLOW"
  | "REASSIGN_SURVEY"
  | "DEPLOY_SURVEY"
  | "RECALL_SURVEY"
  | "REFRESH_SURVEY"
  | "DASHBOARD_ACCESS_RIGHTS"
  | "REVIEW"
  | "VIEW_PRODUCTMANAGER"
  | "VIEW_APCLERK"
  | "VIEW_APCOORDINATOR"
  | "VIEW_OTHERS"
  | "UPDATE_PRODUCTMANAGER"
  | "UPDATE_APCLERK"
  | "UPDATE_APCOORDINATOR"
  | "UPDATE_OTHERS"
  | "UPDATE"
  | "HISTORY"
  | "CREATE_DMS"
  | "EDIT_DMS"
  | "STATUSES"
  | null;

function CrudMenu<Data>(props: {
  menu: Menu;
  closePanel(): void;
  onDone(): void;
  onRightMenuClose(): void;
  noun?: string;
  selectedData: Data[];
  crudItems: CrudItems<Data>;
  menuItems: RightMenuItems;
  filters: React.ReactNode;
  filtersHeaderElement?: React.ReactElement;
  largeRightPanel?: boolean | ((action?: string | null) => boolean);
  idColumn: string;
  additionalData?: any;
  menuActionsCode?: CrudOptions;
  autoOpenPanel?: (val?: any) => void;
  clickedIdIfMultiIds?: string | number;
}) {
  const {
    closePanel,
    onDone,
    onRightMenuClose,
    menu,
    crudItems,
    filters,
    selectedData,
    filtersHeaderElement,
    largeRightPanel,
    idColumn,
    additionalData,
    menuActionsCode,
    autoOpenPanel,
    clickedIdIfMultiIds,
  } = props;

  const {
    menuItems,
    currentMenu: curMenu, //currentMenuRedirected
  } = menu;

  const [currentMenu, setCurrentMenu] = React.useState(curMenu);
  useEffect(() => setCurrentMenu(curMenu), [curMenu]);
  const currentItem = !currentMenu
    ? null
    : crudItems.find(
        item => item.menuCode?.toLowerCase() === currentMenu?.toLowerCase()
      );
  const title = () => {
    if (!currentMenu) {
      return "";
    } else if (currentMenu === "FILTERS") {
      return "Filters";
    } else if (!currentItem) {
      return "";
    }
    return applyMaybeString(currentItem.title, selectedData);
  };

  const content = () => {
    if (currentMenu === "FILTERS") return filters;
    else if (!currentMenu || !currentItem) {
      return null;
    }
    if (crudItems) {
      return currentItem.content(
        onDone,
        selectedData,
        menu,
        additionalData,
        clickedIdIfMultiIds
      );
    }
  };

  let rightPanelOpen =
    crudItems && currentMenu
      ? applyMaybeBoolean(currentItem?.inRightPanel ?? true, selectedData)
      : false;

  const headerElement =
    currentMenu === "FILTERS" ? filtersHeaderElement : undefined;

  const selectedDataIDs: number[] | null =
    selectedData &&
    Array.isArray(selectedData) &&
    selectedData.length > 0 &&
    selectedData[0] &&
    typeof selectedData[0] === "object"
      ? selectedData
          .map((item: any) => {
            if (item[idColumn as keyof typeof item]) {
              return item[idColumn as keyof typeof item];
            } else {
              return null;
            }
          })
          .filter(item => item !== null)
      : null;

  const checkShouldUseLargePanel = () => {
    if (largeRightPanel === undefined) return false;
    if (currentMenu === "FILTERS") return false;
    if (typeof largeRightPanel === "boolean") return largeRightPanel;
    else return largeRightPanel(currentItem?.menuCode);
  };

  const shouldUseLargePanel = checkShouldUseLargePanel();

  useEffect(() => {
    if (menuActionsCode) {
      if (rightPanelOpen) closePanel();
      autoOpenPanel && autoOpenPanel(menuActionsCode);
      // setCurrentMenu(menuActionsCode); //opt
    }
  }, [menuActionsCode]);

  return (
    <React.Fragment>
      {((currentItem && !currentItem.doNotLoadContentInRightPanel) ||
        currentMenu === "FILTERS") && (
        <RightPanel
          title={title()}
          rightPanelContent={rightPanelOpen}
          closePanel={() => {
            closePanel();
          }}
          headerElement={headerElement}
          largeRightPanel={shouldUseLargePanel}
        >
          {content()}
        </RightPanel>
      )}
      {!rightPanelOpen && content()}

      <RightMenu
        toggleAltMenuItems={selectedData.length > 1}
        visible={!menu.fromRowOption && selectedData.length > 0}
        menuItems={menuItems.menuItems}
        alternateMenuItems={menuItems.alternateMenuItems}
        selectedDataIDs={selectedDataIDs}
        additionalDataIDs={additionalData}
        onRightMenuClose={onRightMenuClose}
      />
    </React.Fragment>
  );
}

export default CrudMenu;
